/**
 * Sprint 11.2c — Candidate creation wizard.
 *
 * 4 steps, indicator visible on screen:
 *   1. Upload resume (PDF/DOCX)
 *   2. Review what AI extracted (header / skills / summary)
 *   3. Fill required missing fields (role, department, pipeline, source, notes)
 *   4. Confirm + create
 *
 * Backend contracts used:
 *   POST /api/v1/candidates/parse-resume           (multipart) → { parsed, fields, ... }
 *   POST /api/v1/candidates                        → { id, ... }
 *   POST /api/v1/candidates/{id}/files/upload      (multipart) → persisted original resume
 *   POST /api/v1/candidates/{id}/company-resume/from-parsed → pre-parsed company resume
 */
import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { v1Get, v1Upload, v1Post } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useHasPermission } from '@/auth/permissions';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AssignedHrSelect, useHrUsers } from '@/components/AssignedHrSelect';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Upload, ArrowLeft, ArrowRight, Sparkles, CheckCircle2, FileText, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { classNames } from '@/lib/format';
import { formatMasterOptionLabel } from '@/lib/masterDataLabels';
// Sprint 14.1b — Duplicate-email intake guard.
import { DuplicateEmailModal } from '@/components/evaluation';

const REFRESH_CONFIRM_MESSAGE =
  'Are you sure you want to refresh this page? Any unsaved changes will be lost.';

const INITIAL_FORM = {
  full_name: '', email: '', phone: '',
  role_id: '', department_id: '', sub_department_id: '',
  pipeline_id: '', source: '', notes: '',
  assigned_hr_id: '',
};

const STEPS = [
  { id: 1, label: 'Upload resume', icon: Upload },
  { id: 2, label: 'Review AI parse', icon: Sparkles },
  { id: 3, label: 'Complete details', icon: FileText },
  { id: 4, label: 'Confirm & create', icon: CheckCircle2 },
];

function StepBar({ step }) {
  return (
    <div className="flex items-center gap-2" data-testid="wizard-stepper">
      {STEPS.map((s, idx) => {
        const Icon = s.icon;
        const isDone = step > s.id;
        const isActive = step === s.id;
        return (
          <React.Fragment key={s.id}>
            <div
              className={classNames(
                'flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold',
                isActive && 'border-primary bg-primary/10 text-primary',
                isDone && 'border-emerald-300 bg-emerald-50 text-emerald-700',
                !isActive && !isDone && 'border-border bg-muted text-muted-foreground',
              )}
              data-testid={`wizard-step-${s.id}`}
              data-active={isActive ? 'true' : 'false'}
            >
              <Icon className="h-3.5 w-3.5" />
              <span>Step {s.id}</span>
              <span className="hidden sm:inline">· {s.label}</span>
            </div>
            {idx < STEPS.length - 1 && <span className="h-px w-4 bg-border sm:w-6" />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function CandidateNew() {
  const navigate = useNavigate();
  const canCreate = useHasPermission('candidate.create');
  const { user: currentUser } = useAuth();
  const [step, setStep] = useState(1);

  // Step 1 — resume upload + parse
  const [resumeFile, setResumeFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(null); // AI parse result + fields

  // Step 3 — final form fields
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);

  // Sprint 11.5 cleanup (C) — Assigned HR defaults to the creator.
  // We compute the effective value at render-time (form override → currentUser
  // fallback) rather than mutating state in an effect, which keeps the
  // dataflow simple and avoids any react-hooks/set-state-in-effect concerns.
  const currentUserId = currentUser?.id || '';
  const effectiveAssignedHrId = form.assigned_hr_id || currentUserId;

  const { data: roles = [] } = useQuery({
    queryKey: ['v1-master-roles'],
    queryFn: () => v1Get('/master/roles'),
  });
  const { data: depts = [] } = useQuery({
    queryKey: ['v1-master-departments'],
    queryFn: () => v1Get('/master/departments'),
  });
  const { data: pipelines = [] } = useQuery({
    queryKey: ['v1-pipelines'],
    queryFn: () => v1Get('/pipelines'),
  });
  const { data: hrUsers = [] } = useHrUsers();

  // Sprint 14.1b — Duplicate-email guard. When the user lands on Step 3
  // (after the AI parse), we check the email against existing candidates.
  // If matches exist, we surface the DuplicateEmailModal with two
  // choices: start new evaluation or open the active evaluation.
  const [duplicateMatches, setDuplicateMatches] = React.useState([]);
  const [duplicateOpen, setDuplicateOpen] = React.useState(false);
  const [duplicateChecked, setDuplicateChecked] = React.useState(false);
  const lastCheckedEmailRef = React.useRef('');

  // Refresh / reload guard — wizard state lives only in React memory; a reload
  // snaps the user back to Step 1. Armed once any progress exists.
  const [refreshConfirmOpen, setRefreshConfirmOpen] = React.useState(false);
  const allowUnloadRef = React.useRef(false);
  const hasUnsavedWizardProgress = React.useMemo(() => {
    if (parsing || submitting) return true;
    if (resumeFile || parsed || step > 1) return true;
    return !!(
      form.full_name
      || form.email
      || form.phone
      || form.role_id
      || form.department_id
      || form.sub_department_id
      || form.pipeline_id
      || form.source
      || form.notes
      || form.assigned_hr_id
    );
  }, [parsing, submitting, resumeFile, parsed, step, form]);

  const allowUnloadAndLeave = React.useCallback((leave) => {
    allowUnloadRef.current = true;
    leave();
  }, []);

  const cancelUpload = React.useCallback(() => {
    setStep(1);
    setResumeFile(null);
    setParsed(null);
    setForm({ ...INITIAL_FORM });
    setDuplicateMatches([]);
    setDuplicateOpen(false);
    setDuplicateChecked(false);
    lastCheckedEmailRef.current = '';
    lastAutoPipeRef.current = null;
    allowUnloadAndLeave(() => navigate('/portal/candidates'));
  }, [allowUnloadAndLeave, navigate]);

  React.useEffect(() => {
    if (!hasUnsavedWizardProgress) return undefined;
    const onBeforeUnload = (e) => {
      if (allowUnloadRef.current) return undefined;
      e.preventDefault();
      e.returnValue = REFRESH_CONFIRM_MESSAGE;
      return REFRESH_CONFIRM_MESSAGE;
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [hasUnsavedWizardProgress]);

  // Keyboard reload (F5 / Ctrl|Cmd+R) — show our dialog so Cancel keeps step + data.
  // Browser chrome reload still uses the beforeunload confirm above.
  React.useEffect(() => {
    if (!hasUnsavedWizardProgress) return undefined;
    const onKeyDown = (e) => {
      if (allowUnloadRef.current) return;
      const key = e.key?.toLowerCase?.() || '';
      const isReload = e.key === 'F5' || ((e.ctrlKey || e.metaKey) && key === 'r');
      if (!isReload) return;
      e.preventDefault();
      setRefreshConfirmOpen(true);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [hasUnsavedWizardProgress]);

  const confirmRefresh = () => {
    allowUnloadRef.current = true;
    setRefreshConfirmOpen(false);
    window.location.reload();
  };

  const runDuplicateCheck = React.useCallback(async (email) => {
    const normalized = (email || '').trim().toLowerCase();
    if (!normalized || !/^\S+@\S+\.\S+$/.test(normalized)) return;
    if (lastCheckedEmailRef.current === normalized) return; // de-dupe
    lastCheckedEmailRef.current = normalized;
    try {
      const res = await v1Get(
        `/candidates/check-duplicate?email=${encodeURIComponent(normalized)}`,
      );
      const matches = res?.matches || [];
      setDuplicateMatches(matches);
      setDuplicateChecked(true);
      if (matches.length > 0) {
        setDuplicateOpen(true);
      }
    } catch {
      // Non-fatal — if the check fails we just continue with the normal flow.
      setDuplicateChecked(true);
    }
  }, []);

  const handleDuplicateChoice = ({ action, candidate_id, evaluation_id }) => {
    setDuplicateOpen(false);
    if (action === 'new_eval') {
      allowUnloadAndLeave(() => navigate(`/portal/candidates/${candidate_id}?action=new-eval`));
    } else if (action === 'open_active') {
      allowUnloadAndLeave(() => navigate(
        `/portal/candidates/${candidate_id}${evaluation_id ? `?eval=${evaluation_id}` : ''}`,
      ));
    }
  };

  const set = (k) => (v) =>
    setForm((f) => ({ ...f, [k]: typeof v === 'object' && v?.target ? v.target.value : v }));

  // Auto-pick a sensible pipeline when role + dept are selected
  const autoPipelineId = React.useMemo(() => {
    if (!form.role_id || !form.department_id) return null;
    const exact = pipelines.find((p) => {
      const pRoles = Array.isArray(p.role_ids) && p.role_ids.length
        ? p.role_ids
        : (p.role_id ? [p.role_id] : []);
      return pRoles.includes(form.role_id) && p.department_id === form.department_id;
    });
    return exact?.id || pipelines.find((p) => p.department_id === form.department_id)?.id || null;
  }, [pipelines, form.role_id, form.department_id]);
  // Auto-bind pipeline_id when role+department selection produces a unique
  // match. Stored in a ref to avoid the react-hooks/set-state-in-effect rule;
  // we only patch state if the value actually changed.
  const lastAutoPipeRef = React.useRef(null);
  React.useEffect(() => {
    if (!autoPipelineId) return;
    if (form.pipeline_id) return;
    if (lastAutoPipeRef.current === autoPipelineId) return;
    lastAutoPipeRef.current = autoPipelineId;
    setForm((f) => (f.pipeline_id ? f : { ...f, pipeline_id: autoPipelineId }));
  }, [autoPipelineId, form.pipeline_id]);

  const handleUpload = async (file) => {
    if (!file) return;
    if (!/\.(pdf|docx)$/i.test(file.name)) {
      toast.error('Only PDF or DOCX resumes are supported.');
      return;
    }
    setResumeFile(file);
    setParsing(true);
    try {
      const res = await v1Upload('/candidates/parse-resume', file);
      setParsed(res);
      // Pre-fill what we can from the parsed payload
      setForm((f) => ({
        ...f,
        full_name: f.full_name || res.fields?.full_name || '',
        email: f.email || res.fields?.email || '',
        phone: f.phone || res.fields?.phone || '',
      }));
      toast.success('Resume parsed — review the extracted data on the next step.');
      setStep(2);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Resume parsing failed.');
    } finally {
      setParsing(false);
    }
  };

  const canStep3Continue =
    !!form.full_name && !!form.email && !!form.role_id && !!form.department_id
    && !!form.pipeline_id && !!effectiveAssignedHrId;

  const submit = async () => {
    if (!canStep3Continue) {
      toast.error('Full name, email, role, department, pipeline and assigned HR are required.');
      return;
    }
    setSubmitting(true);
    try {
      const created = await v1Post('/candidates', {
        full_name: form.full_name,
        email: form.email || null,
        phone: form.phone || null,
        role_id: form.role_id,
        department_id: form.department_id,
        sub_department_id: form.sub_department_id || null,
        pipeline_id: form.pipeline_id,
        assigned_hr_id: effectiveAssignedHrId || null,
      });

      // Sprint 11.5 cleanup (Issue 5): persist the original resume immediately
      // after the candidate row is created. Use the backend-proxied upload path
      // (same as CandidateDetail) — browser PUT to presigned S3 URLs fails when
      // the bucket has no CORS rule, which left wizard-created candidates with
      // no original file on the Resume tab.
      // Failure here is non-fatal — HR can still upload from the detail page —
      // but we surface a clear toast so they know to retry.
      let uploadedFileId = null;
      if (resumeFile) {
        try {
          const uploaded = await v1Upload(
            `/candidates/${created.id}/files/upload`,
            resumeFile,
            { kind: 'resume', evaluation_id: created.evaluation_id },
          );
          uploadedFileId = uploaded.id;
        } catch (uploadErr) {
          // Non-fatal: candidate exists, just no PDF persisted.
          const detail = uploadErr?.response?.data?.detail
            || uploadErr?.message || 'Resume file upload failed';
          toast.warning(
            typeof detail === 'string'
              ? `Candidate created, but resume PDF upload failed: ${detail}. You can retry from the Resume tab.`
              : 'Candidate created, but resume PDF upload failed. Retry from the Resume tab.'
          );
        }
      }

      // Sprint 11.5 cleanup (Issue 6): persist the already-parsed AI
      // payload so the candidate immediately shows up in Resume Enrichment.
      // We did the AI parse once in Step 1; calling Generate on the detail
      // page later would re-run AI parse. Failure here is non-fatal — HR can
      // click Generate manually from the Resume tab to recover.
      if (parsed?.parsed && parsed?.model_version) {
        try {
          await v1Post(`/candidates/${created.id}/company-resume/from-parsed`, {
            payload: parsed.parsed,
            ai_model_version: parsed.model_version,
            source_file_id: uploadedFileId,
            evaluation_id: created.evaluation_id,
          });
        } catch (persistErr) {
          const detail = persistErr?.response?.data?.detail
            || persistErr?.message || 'Standardised resume persistence failed';
          toast.warning(
            typeof detail === 'string'
              ? `Candidate created, but standardised resume couldn't be saved: ${detail}. Click Generate from the Resume tab to retry.`
              : 'Candidate created, but standardised resume couldn\'t be saved. Click Generate from the Resume tab to retry.'
          );
        }
      }

      toast.success(`Candidate ${created.full_name} created`);
      const evalQs = created.evaluation_id
        ? `?eval=${encodeURIComponent(created.evaluation_id)}`
        : '';
      allowUnloadAndLeave(() => navigate(`/portal/candidates/${created.id}${evalQs}`));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not create candidate.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!canCreate) {
    return <Navigate to="/portal/candidates" replace />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="New Candidate"
        subtitle="A guided 4-step flow. AI parses the resume; HR confirms and creates."
        actions={
          <Button variant="ghost" className="gap-1.5" onClick={() => navigate(-1)} data-testid="wizard-back-to-list">
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
        }
      />
      <StepBar step={step} />

      {/* STEP 1 — Upload */}
      {step === 1 && (
        <Card className="card-elevated">
          <CardContent className="p-8 text-center">
            {!parsing ? (
              <>
                <Upload className="mx-auto h-10 w-10 text-muted-foreground" />
                <p className="mt-3 text-base font-semibold">Drop the candidate&apos;s resume</p>
                <p className="mt-1 text-xs text-muted-foreground">PDF or DOCX · up to 10 MB</p>
                <label className="mt-5 inline-flex cursor-pointer items-center gap-2 rounded-md border bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90">
                  <input
                    type="file"
                    accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    className="hidden"
                    onChange={(e) => handleUpload(e.target.files?.[0] || null)}
                    data-testid="wizard-resume-input"
                  />
                  <Upload className="h-4 w-4" />
                  Choose resume file
                </label>
                <p className="mt-3 text-[11px] text-muted-foreground">
                  We&apos;ll extract the text and ask AI to structure it. This typically takes <strong>20–30 seconds</strong>.
                </p>
              </>
            ) : (
              <ParseProgress />
            )}
          </CardContent>
        </Card>
      )}

      {/* STEP 2 — Review AI parse */}
      {step === 2 && parsed && (
        <Card className="card-elevated">
          <CardContent className="space-y-4 p-6" data-testid="wizard-step-2-review">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold">AI-extracted data</p>
                <p className="text-xs text-muted-foreground">
                  {parsed.filename}
                </p>
              </div>
              <Badge variant="outline" className="gap-1">
                <Sparkles className="h-3 w-3" /> AI parsed
              </Badge>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Info label="Full name" value={parsed.fields?.full_name} />
              {/* Sprint 11.5 cleanup (D) — email is required to proceed. If
                  AI couldn't find one we render an inline editor so HR
                  can fill it without losing the parsed context. */}
              {parsed.fields?.email ? (
                <Info label="Email" value={parsed.fields.email} />
              ) : (
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-rose-700">
                    Email <span className="text-rose-600">*</span> (not found by AI)
                  </p>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={set('email')}
                    placeholder="candidate@example.com"
                    className="mt-1 border-rose-300 focus-visible:ring-rose-400"
                    data-testid="wizard-email-fallback-input"
                    autoFocus
                  />
                  <p className="mt-1 text-[11px] text-rose-700">
                    A valid email is required to move this candidate forward.
                  </p>
                </div>
              )}
              <Info label="Phone" value={parsed.fields?.phone} />
              <Info label="Headline" value={parsed.fields?.headline} />
            </div>
            {parsed.fields?.summary && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground">Summary</p>
                <p className="mt-1 text-sm leading-relaxed">{parsed.fields.summary}</p>
              </div>
            )}
            {parsed.fields?.skills?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground">Skills detected</p>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {parsed.fields.skills.map((s) => (
                    <span key={s} className="rounded-md bg-secondary px-2 py-0.5 text-xs font-medium">
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {!parsed.fields?.full_name && (
              <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>
                  AI couldn&apos;t reliably extract a name. You&apos;ll need to type it manually on the next step.
                </span>
              </div>
            )}
            <div className="flex items-center justify-between pt-2">
              <Button variant="ghost" onClick={() => setStep(1)} data-testid="wizard-back-step-1">
                <ArrowLeft className="mr-1.5 h-4 w-4" /> Re-upload
              </Button>
              <Button
                onClick={() => {
                  // Sprint 14.1b — duplicate-email guard runs once when the
                  // user advances from Step 2 to Step 3.
                  runDuplicateCheck(form.email);
                  setStep(3);
                }}
                disabled={!form.email || !/^\S+@\S+\.\S+$/.test(form.email)}
                data-testid="wizard-next-step-3"
              >
                Continue <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* STEP 3 — Complete required fields */}
      {step === 3 && (
        <Card className="card-elevated">
          <CardContent className="space-y-4 p-6" data-testid="wizard-step-3-form">
            <p className="text-sm font-semibold">Confirm &amp; complete the candidate&apos;s details</p>
            <p className="text-xs text-muted-foreground">
              Fields marked <span className="text-rose-600">*</span> are required.
            </p>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field label="Full Name" required>
                <Input
                  value={form.full_name} onChange={set('full_name')}
                  placeholder="e.g. Aanya Kapoor"
                  data-testid="wizard-name-input"
                />
              </Field>
              <Field label="Email" required>
                <Input
                  value={form.email} onChange={set('email')}
                  placeholder="aanya@example.com"
                  data-testid="wizard-email-input"
                />
              </Field>
              <Field label="Phone">
                <Input value={form.phone} onChange={set('phone')} placeholder="+91 98XXX XXXXX" />
              </Field>
              <Field label="Source / Channel">
                <Input
                  value={form.source} onChange={set('source')}
                  placeholder="Referral / LinkedIn / Job board"
                />
              </Field>
              <Field label="Role applied for" required>
                <Select value={form.role_id} onValueChange={set('role_id')}>
                  <SelectTrigger data-testid="wizard-role-select">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.filter((r) => r.status === 'active').map((r) => (
                      <SelectItem key={r.id} value={r.id}>{formatMasterOptionLabel(r)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Department" required>
                <Select
                  value={form.department_id}
                  onValueChange={(v) => setForm((f) => ({ ...f, department_id: v, sub_department_id: '' }))}
                >
                  <SelectTrigger data-testid="wizard-department-select">
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    {depts.filter((d) => d.status === 'active').map((d) => (
                      <SelectItem key={d.id} value={d.id}>{formatMasterOptionLabel(d)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Pipeline" required>
                <Select value={form.pipeline_id} onValueChange={set('pipeline_id')}>
                  <SelectTrigger data-testid="wizard-pipeline-select">
                    <SelectValue placeholder={form.department_id ? 'Select pipeline' : 'Pick role & department first'} />
                  </SelectTrigger>
                  <SelectContent>
                    {pipelines.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.label || p.pipeline_key}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Assigned HR" required>
                <AssignedHrSelect
                  value={effectiveAssignedHrId || ''}
                  onChange={(v) => setForm((f) => ({ ...f, assigned_hr_id: v }))}
                  placeholder="Select HR owner"
                  testId="wizard-assigned-hr-select"
                />
              </Field>

              <Field label="Internal notes" className="md:col-span-2">
                <Textarea
                  rows={3} value={form.notes} onChange={set('notes')}
                  placeholder="Not visible to candidate or client"
                />
              </Field>
            </div>
            <div className="flex items-center justify-between pt-2">
              <Button variant="ghost" onClick={() => setStep(2)} data-testid="wizard-back-step-2">
                <ArrowLeft className="mr-1.5 h-4 w-4" /> Back
              </Button>
              <Button
                onClick={() => setStep(4)}
                disabled={!canStep3Continue}
                data-testid="wizard-next-step-4"
              >
                Continue <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* STEP 4 — Confirm + create */}
      {step === 4 && (
        <Card className="card-elevated">
          <CardContent className="space-y-4 p-6" data-testid="wizard-step-4-confirm">
            <p className="text-sm font-semibold">Review & create</p>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Info label="Full name" value={form.full_name} />
              <Info label="Email" value={form.email} />
              <Info label="Phone" value={form.phone} />
              <Info
                label="Role"
                value={roles.find((r) => r.id === form.role_id)?.name}
              />
              <Info
                label="Department"
                value={depts.find((d) => d.id === form.department_id)?.name}
              />
              <Info
                label="Pipeline"
                value={pipelines.find((p) => p.id === form.pipeline_id)?.label}
              />
              <Info
                label="Assigned HR"
                value={hrUsers.find((u) => u.id === effectiveAssignedHrId)?.full_name}
              />
              <Info label="Source" value={form.source} className="md:col-span-2" />
              {form.notes && <Info label="Internal notes" value={form.notes} className="md:col-span-2" />}
            </div>
            {resumeFile && (
              <div className="rounded-xl border bg-secondary/30 p-3 text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">Resume:</span> {resumeFile.name}
                <span className="ml-2 text-[10px]">
                  ({(resumeFile.size / 1024).toFixed(0)} KB · will be uploaded to S3 on create)
                </span>
              </div>
            )}
            <div className="flex items-center justify-between pt-2">
              <Button variant="ghost" onClick={() => setStep(3)} data-testid="wizard-back-step-3">
                <ArrowLeft className="mr-1.5 h-4 w-4" /> Back
              </Button>
              <Button
                onClick={submit}
                disabled={submitting}
                data-testid="wizard-create-candidate-button"
              >
                {submitting ? 'Creating…' : 'Create candidate'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sprint 14.1b — Duplicate-email modal. Appears when the email entered
          in Step 3 matches an existing candidate. The prompt lets the user
          start a new evaluation or open the existing active one. */}
      <DuplicateEmailModal
        open={duplicateOpen}
        onOpenChange={setDuplicateOpen}
        matches={duplicateMatches}
        onChoice={handleDuplicateChoice}
        onCancel={cancelUpload}
      />

      <AlertDialog open={refreshConfirmOpen} onOpenChange={setRefreshConfirmOpen}>
        <AlertDialogContent data-testid="wizard-refresh-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Refresh this page?</AlertDialogTitle>
            <AlertDialogDescription>
              {REFRESH_CONFIRM_MESSAGE}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="wizard-refresh-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRefresh}
              data-testid="wizard-refresh-confirm-continue"
            >
              Refresh
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Inline hint when a duplicate was detected and the user dismissed
          the modal without resolving it. */}
      {duplicateChecked && duplicateMatches.length > 0 && !duplicateOpen && (
        <div
          className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900"
          data-testid="duplicate-email-inline-hint"
        >
          <strong>Heads up:</strong> this email matches{' '}
          {duplicateMatches.length === 1
            ? `${duplicateMatches[0].full_name}.`
            : `${duplicateMatches.length} existing candidates.`}{' '}
          <button
            type="button"
            className="font-medium underline"
            onClick={() => setDuplicateOpen(true)}
            data-testid="duplicate-email-reopen-button"
          >
            Resolve duplicate
          </button>
        </div>
      )}
    </div>
  );
}

function Field({ label, required, children, className }) {
  return (
    <div className={className}>
      <label className="text-xs font-medium text-muted-foreground">
        {label} {required && <span className="text-rose-600">*</span>}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Info({ label, value, className }) {
  return (
    <div className={className}>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className="mt-0.5 text-sm">{value || <span className="text-muted-foreground">—</span>}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ParseProgress — Sprint 11.5 UX fix.
// Resume parsing takes 25–30s (PDF text extraction + AI structuring). Without
// a clear progress signal users assume the upload is broken and abandon. This
// component shows: a spinner, elapsed seconds, and a 3-stage timeline.
// ---------------------------------------------------------------------------
function ParseProgress() {
  const [elapsed, setElapsed] = React.useState(0);
  React.useEffect(() => {
    const start = Date.now();
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 250);
    return () => clearInterval(t);
  }, []);

  // Heuristic stage progression based on elapsed seconds — matches typical
  // backend timing (PDF extract ~2s, AI parse ~20–28s).
  const stage = elapsed < 3 ? 0 : elapsed < 28 ? 1 : 2;
  const stages = [
    { label: 'Extracting text from PDF', detail: '' },
    { label: 'Structuring resume with AI', detail: '' },
    { label: 'Finalising', detail: 'mapping to the company template' },
  ];

  return (
    <div className="mx-auto max-w-md" data-testid="parse-progress">
      <div className="flex items-center justify-center gap-2">
        <Sparkles className="h-5 w-5 animate-pulse text-primary" />
        <p className="text-base font-semibold">Parsing resume with AI</p>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Typically 20–30 seconds · elapsed <span className="font-mono font-semibold">{elapsed}s</span>
      </p>

      <ul className="mt-5 space-y-3 text-left">
        {stages.map((s, i) => {
          const state = i < stage ? 'done' : i === stage ? 'active' : 'pending';
          return (
            <li key={i} className="flex items-start gap-3" data-testid={`parse-stage-${i}-${state}`}>
              <span
                className={
                  state === 'done'
                    ? 'mt-1.5 h-2 w-2 shrink-0 rounded-full bg-emerald-500'
                    : state === 'active'
                    ? 'mt-1 h-2.5 w-2.5 shrink-0 animate-pulse rounded-full bg-primary ring-4 ring-primary/20'
                    : 'mt-1.5 h-2 w-2 shrink-0 rounded-full bg-muted'
                }
                aria-hidden="true"
              />
              <div className="flex-1">
                <p className={`text-sm font-medium ${state === 'pending' ? 'text-muted-foreground' : ''}`}>
                  {s.label}
                </p>
                <p className="text-[11px] text-muted-foreground">{s.detail}</p>
              </div>
            </li>
          );
        })}
      </ul>

      {elapsed > 35 && (
        <p className="mt-4 rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-[11px] text-amber-700 dark:text-amber-300">
          Taking longer than usual. Large resumes (10+ pages) can push parsing past 45s. Please wait — do not refresh.
        </p>
      )}
    </div>
  );
}
